import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { superadminAPI } from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import {
  HiOutlineUsers,
  HiOutlineMagnifyingGlass,
  HiOutlineFunnel,
  HiOutlineArrowDownTray,
  HiOutlineEye,
  HiOutlinePause,
  HiOutlinePlay,
  HiOutlineArrowsUpDown,
  HiOutlineXMark,
  HiOutlineTrash
} from 'react-icons/hi2'

const SubscribersPage = () => {
  const navigate = useNavigate()
  const { showSuccess, showError } = useAlerts()
  const [loading, setLoading] = useState(true)
  const [accounts, setAccounts] = useState([])
  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    current_page: 1,
    next: null,
    previous: null
  })
  const [filters, setFilters] = useState({
    search: '',
    is_owner: '',
    country: '',
    is_active: '',
    sort_by: '-created_at',
    page: 1
  })
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [showActionModal, setShowActionModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [accountDetails, setAccountDetails] = useState(null)
  const [actionType, setActionType] = useState('')
  const [actionReason, setActionReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)

  useEffect(() => {
    fetchAccounts()
  }, [filters])

  const fetchAccounts = async () => {
    try {
      setLoading(true)
      const response = await superadminAPI.getAccounts(filters)
      
      if (response.success) {
        setAccounts(response.data.data || [])
        // Update pagination info
        if (response.data.pagination) {
          setPagination(response.data.pagination)
        }
      } else {
        showError('Failed to load accounts')
      }
    } catch (error) {
      console.error('Error fetching accounts:', error)
      showError('Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const fetchAccountDetails = async (accountId) => {
    try {
      setDetailsLoading(true)
      const response = await superadminAPI.getAccountDetail(accountId)
      
      if (response.success) {
        setAccountDetails(response.data.data)
      } else {
        showError('Failed to load account details')
      }
    } catch (error) {
      console.error('Error fetching account details:', error)
      showError('Failed to load account details')
    } finally {
      setDetailsLoading(false)
    }
  }

  const handleViewDetails = async (account) => {
    setSelectedAccount(account)
    setShowDetailsModal(true)
    await fetchAccountDetails(account.id)
  }

  const handleAction = async () => {
    if (!selectedAccount || !actionType) return
    
    try {
      setActionLoading(true)
      const response = await superadminAPI.accountAction(
        selectedAccount.id,
        actionType,
        actionReason
      )
      
      if (response.success) {
        const actionMessages = {
          'suspend': 'suspended',
          'reactivate': 'reactivated',
          'delete': 'deleted'
        }
        showSuccess(`Account ${actionMessages[actionType]} successfully`)
        setShowActionModal(false)
        setSelectedAccount(null)
        setActionType('')
        setActionReason('')
        fetchAccounts()
      } else {
        showError(response.error?.message || 'Action failed')
      }
    } catch (error) {
      console.error('Error performing action:', error)
      showError('Action failed')
    } finally {
      setActionLoading(false)
    }
  }

  const handleExport = async (type) => {
    try {
      const response = type === 'owners' 
        ? await superadminAPI.exportOwners()
        : await superadminAPI.exportEmployees()
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      showSuccess(`${type} exported successfully`)
    } catch (error) {
      console.error('Error exporting:', error)
      showError('Export failed')
    }
  }

  const getStatusBadge = (account) => {
    if (account.is_deleted) {
      return (
        <div>
          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-danger rounded-full">Deleted</span>
          {account.deleted_reason && (
            <div className="text-xs text-gray-600 mt-1 italic">{account.deleted_reason}</div>
          )}
        </div>
      )
    }
    if (account.is_disabled) {
      return (
        <div>
          <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-warning rounded-full">Suspended</span>
          {account.disabled_reason && (
            <div className="text-xs text-gray-600 mt-1 italic">{account.disabled_reason}</div>
          )}
        </div>
      )
    }
    if (account.is_active) {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-success rounded-full">Active</span>
    }
    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">Inactive</span>
  }

  const sortOptions = [
    { value: '-created_at', label: 'Newest First' },
    { value: 'created_at', label: 'Oldest First' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
    { value: 'owner__name', label: 'Owner Name (A-Z)' },
    { value: '-owner__name', label: 'Owner Name (Z-A)' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscribers Management</h1>
          <p className="text-gray-600 mt-1">Manage all accounts and subscriptions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('owners')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <HiOutlineArrowDownTray className="w-5 h-5" />
            Export Owners
          </button>
          <button
            onClick={() => handleExport('employees')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <HiOutlineArrowDownTray className="w-5 h-5" />
            Export Employees
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="space-y-4">
          {/* First Row: Search and Account Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <HiOutlineMagnifyingGlass className="inline w-4 h-4 mr-1" />
                Search
              </label>
              <input
                type="text"
                placeholder="Search by name, email, mobile, or account name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <HiOutlineFunnel className="inline w-4 h-4 mr-1" />
                Account Type
              </label>
              <select
                value={filters.is_owner}
                onChange={(e) => setFilters({ ...filters, is_owner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">All Users</option>
                <option value="true">Owners</option>
                <option value="false">Employees</option>
              </select>
            </div>
          </div>

          {/* Second Row: Status, Sort, Clear */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.is_active}
                onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive/Suspended</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <HiOutlineArrowsUpDown className="inline w-4 h-4 mr-1" />
                Sort By
              </label>
              <select
                value={filters.sort_by}
                onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({ search: '', is_owner: '', country: '', is_active: '', sort_by: '-created_at' })}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      {!loading && (
        <div className="text-sm text-gray-600">
          Found <span className="font-medium text-gray-900">{accounts.length}</span> account(s)
        </div>
      )}

      {/* Accounts Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12">
            <HiOutlineUsers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No accounts found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Account Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Owner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{account.name}</div>
                        <div className="text-xs text-gray-500">ID: {account.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{account.owner?.name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">
                          {account.owner?.country?.name || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">{account.owner?.email || 'N/A'}</div>
                        <div className="text-gray-500">{account.owner?.mobile || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(account)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(account.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(account)}
                          className="text-brand hover:text-blue-900 p-2 rounded-lg hover:bg-info-soft transition-colors"
                          title="View Details"
                        >
                          <HiOutlineEye className="w-5 h-5" />
                        </button>
                        
                        {account.is_deleted ? (
                          <span className="text-xs text-gray-500 px-2">Deleted</span>
                        ) : account.is_disabled ? (
                          <button
                            onClick={() => {
                              setSelectedAccount(account)
                              setActionType('reactivate')
                              setShowActionModal(true)
                            }}
                            className="text-success hover:text-green-900 p-2 rounded-lg hover:bg-success-soft transition-colors"
                            title="Reactivate"
                          >
                            <HiOutlinePlay className="w-5 h-5" />
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setSelectedAccount(account)
                                setActionType('suspend')
                                setShowActionModal(true)
                              }}
                              className="text-warning hover:text-amber-900 p-2 rounded-lg hover:bg-warning-soft transition-colors"
                              title="Suspend"
                            >
                              <HiOutlinePause className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAccount(account)
                                setActionType('delete')
                                setShowActionModal(true)
                              }}
                              className="text-danger hover:text-red-900 p-2 rounded-lg hover:bg-danger-soft transition-colors"
                              title="Delete"
                            >
                              <HiOutlineTrash className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t pt-4 px-6">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{accounts.length}</span> of{' '}
                  <span className="font-medium">{pagination.count}</span> accounts
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={!pagination.previous}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(pagination.total_pages)].map((_, idx) => {
                      const pageNum = idx + 1
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === pagination.total_pages ||
                        (pageNum >= pagination.current_page - 1 && pageNum <= pagination.current_page + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              pageNum === pagination.current_page
                                ? 'bg-brand-primary text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )
                      } else if (
                        pageNum === pagination.current_page - 2 ||
                        pageNum === pagination.current_page + 2
                      ) {
                        return <span key={pageNum} className="px-2 text-gray-400">...</span>
                      }
                      return null
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={!pagination.next}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showDetailsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Account Details</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/superadmin/subscribers/${selectedAccount.id}`)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Full Details Page
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false)
                    setSelectedAccount(null)
                    setAccountDetails(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <HiOutlineXMark className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {detailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : accountDetails ? (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Account Information</h4>
                      <dl className="space-y-2 text-sm">
                        <div>
                          <dt className="text-gray-500">Account Name</dt>
                          <dd className="text-gray-900 font-medium">{accountDetails.account.name}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Account ID</dt>
                          <dd className="text-gray-900">{accountDetails.account.id}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Created</dt>
                          <dd className="text-gray-900">{new Date(accountDetails.account.created_at).toLocaleString()}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Last Activity</dt>
                          <dd className="text-gray-900">
                            {accountDetails.activity.last_activity 
                              ? new Date(accountDetails.activity.last_activity).toLocaleString()
                              : 'Never'}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Owner Information</h4>
                      <dl className="space-y-2 text-sm">
                        <div>
                          <dt className="text-gray-500">Name</dt>
                          <dd className="text-gray-900 font-medium">{accountDetails.account.owner.name}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Email</dt>
                          <dd className="text-gray-900">{accountDetails.account.owner.email}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Mobile</dt>
                          <dd className="text-gray-900">{accountDetails.account.owner.mobile}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Country</dt>
                          <dd className="text-gray-900">{accountDetails.account.owner.country?.name || 'N/A'}</dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Account Statistics</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-info-soft p-3 rounded-lg">
                        <div className="text-2xl font-bold text-brand">{accountDetails.activity.total_clients}</div>
                        <div className="text-xs text-gray-600">Clients</div>
                      </div>
                      <div className="bg-brand-soft p-3 rounded-lg">
                        <div className="text-2xl font-bold text-brand">{accountDetails.activity.total_cases}</div>
                        <div className="text-xs text-gray-600">Cases</div>
                      </div>
                      <div className="bg-success-soft p-3 rounded-lg">
                        <div className="text-2xl font-bold text-success">{accountDetails.activity.total_sessions}</div>
                        <div className="text-xs text-gray-600">Sessions</div>
                      </div>
                      <div className="bg-warning-soft p-3 rounded-lg">
                        <div className="text-2xl font-bold text-warning">{accountDetails.activity.total_documents}</div>
                        <div className="text-xs text-gray-600">Documents</div>
                      </div>
                    </div>
                  </div>

                  {/* Storage */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Storage Usage</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-lg font-semibold text-gray-900">
                        {accountDetails.storage.total_gb} GB
                      </div>
                      {accountDetails.storage.limit_bytes && (
                        <div className="text-sm text-gray-600">
                          of {(accountDetails.storage.limit_bytes / (1024 ** 3)).toFixed(2)} GB limit
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Employees */}
                  {accountDetails.employees && accountDetails.employees.count > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">
                        Employees/Followers ({accountDetails.employees.count})
                      </h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Role</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {accountDetails.employees.list.map((emp, idx) => (
                              <tr key={idx}>
                                <td className="px-4 py-2">{emp.name}</td>
                                <td className="px-4 py-2 text-gray-600">{emp.email}</td>
                                <td className="px-4 py-2">
                                  <span className="px-2 py-1 bg-blue-100 text-brand rounded text-xs">
                                    {emp.role}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  {emp.is_active ? (
                                    <span className="text-success">Active</span>
                                  ) : (
                                    <span className="text-gray-400">Inactive</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Subscription Info */}
                  {accountDetails.subscription.current && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Current Subscription</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="font-medium text-gray-900">
                          {accountDetails.subscription.current.plan_name}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Status: <span className="font-medium">{accountDetails.subscription.current.status}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-600">
                  Failed to load details
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {actionType === 'suspend' && 'Suspend Account'}
              {actionType === 'reactivate' && 'Reactivate Account'}
              {actionType === 'delete' && 'Delete Account'}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Account: <span className="font-medium text-gray-900">{selectedAccount.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Owner: <span className="font-medium text-gray-900">{selectedAccount.owner?.name}</span>
              </p>
            </div>

            {actionType === 'delete' && (
              <div className="mb-4 p-4 bg-danger-soft border border-danger/30 rounded-lg">
                <p className="text-sm text-red-800 font-medium">⚠️ Warning</p>
                <p className="text-sm text-danger mt-1">
                  This will delete the account and prevent the user from accessing any data. This action is reversible only by a superadmin.
                </p>
              </div>
            )}

            {(actionType === 'suspend' || actionType === 'delete') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason {actionType === 'delete' ? '(required)' : '(optional)'}
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={`Enter reason for ${actionType}...`}
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleAction}
                disabled={actionLoading || (actionType === 'delete' && !actionReason.trim())}
                className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                  actionType === 'suspend'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : actionType === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setShowActionModal(false)
                  setSelectedAccount(null)
                  setActionType('')
                  setActionReason('')
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscribersPage
